/* ─────────────────────────────────────────────────────────────
   EARNINGS-NOTE TABS — shared config & formatters

   Backs the four per-stock tabs added 2026-08-10:
     • Earnings Review    GET /api/v1/stocks/{ticker}/earnings-review
     • Sector Context     GET /api/v1/stocks/{ticker}/sector-context
     • Thesis             GET /api/v1/stocks/{ticker}/thesis
     • Screen & Catalysts GET /api/v1/stocks/{ticker}/screen-catalysts

   NULL RULE (applies to every field on all four tabs):
   any field can be null and any list empty when an engine doesn't cover
   the stock — render what's present, hide what's null, never show "N/A".
───────────────────────────────────────────────────────────── */

/* ── Management-sentiment bands ────────────────────────────────
   Cutoffs are the API's canonical rubric; colours are lifted from the
   authoritative band strip in the design mock (MU_Q3FY26_Earnings_Note).
   `width` is the strip segment width in % so the strip is proportional
   to the 0–100 scale.
─────────────────────────────────────────────────────────────── */
export const SENTIMENT_BANDS = Object.freeze([
  { key: "Defensive",    label: "Defensive / Negative",           min: 0,  max: 29,  width: 29, color: "#8A3B2E" },
  { key: "Cautious",     label: "Cautious",                       min: 30, max: 44,  width: 15, color: "#A85535" },
  { key: "Neutral",      label: "Neutral / Mixed",                min: 45, max: 54,  width: 10, color: "#9A6B15" },
  { key: "Constructive", label: "Constructive",                   min: 55, max: 69,  width: 15, color: "#6E8B3D" },
  { key: "Bullish",      label: "Bullish / Confident",            min: 70, max: 84,  width: 15, color: "#3E9668" },
  { key: "Very Bullish", label: "Very Bullish / High Conviction",  min: 85, max: 100, width: 16, color: "#2E7D4F" },
]);

/** Band object for a 0–100 composite score, or null when unscored. */
export const bandForScore = (score) => {
  const s = Number(score);
  if (score == null || Number.isNaN(s)) return null;
  if (s >= 85) return SENTIMENT_BANDS[5];
  if (s >= 70) return SENTIMENT_BANDS[4];
  if (s >= 55) return SENTIMENT_BANDS[3];
  if (s >= 45) return SENTIMENT_BANDS[2];
  if (s >= 30) return SENTIMENT_BANDS[1];
  return SENTIMENT_BANDS[0];
};

/**
 * Prefer the API's own `band` string; fall back to deriving it from the score
 * so a band we don't recognise still renders with the right colour.
 */
export const resolveBand = (bandName, score) => {
  if (bandName) {
    const hit = SENTIMENT_BANDS.find(
      (b) => b.key.toLowerCase() === String(bandName).trim().toLowerCase()
    );
    if (hit) return hit;
  }
  return bandForScore(score);
};

/** Rubric rule — must be displayed with every sentiment score. */
export const SENTIMENT_DISCLAIMER =
  "Scores management tone, not results — not a return forecast.";

/* ── FA grade colours (same map used by the Peers / Ratings tabs) ── */
export const gradeBackground = (grade) => {
  const map = {
    "A+": "var(--color-grade-a-plus)", A: "var(--color-grade-a)", "A-": "var(--color-grade-a-minus)",
    "B+": "var(--color-grade-b-plus)", B: "var(--color-grade-b)", "B-": "var(--color-grade-b-minus)",
    "C+": "var(--color-grade-c-plus)", C: "var(--color-grade-c)", "C-": "var(--color-grade-c-minus)",
    "D+": "var(--color-grade-d-plus)", D: "var(--color-grade-d)", "D-": "var(--color-grade-d-minus)",
    E: "var(--color-grade-e)",
    F: "var(--color-grade-f)",
  };
  return map[grade] ?? "var(--color-grade-unknown)";
};

/* ── Factor rows for the Screen & Catalysts tab ──
   `risk` is PRICE risk; `balance_sheet_quality` is the 6th factor and stays
   null until the model backend pushes it (row hidden while null).           */
export const SCREEN_FACTOR_ROWS = Object.freeze([
  { key: "valuation",             label: "Valuation" },
  { key: "growth",                label: "Growth" },
  { key: "momentum",              label: "Momentum" },
  { key: "financial_profit",      label: "Financial / Profitability" },
  { key: "risk",                  label: "Risk (price)" },
  { key: "balance_sheet_quality", label: "Balance Sheet Quality" },
]);

/* ── Fair-value signal → tone ──
   Signal values: Accumulate / Hold / Watch (FV uncertain) / Trim /
   Trim (FV uncertain) / Avoid (value-trap risk) / N/A (...)              */
export const signalTone = (signal) => {
  if (!signal) return "neutral";
  const s = String(signal).toLowerCase();
  if (s.startsWith("accumulate")) return "pos";
  if (s.startsWith("avoid")) return "neg";
  if (s.startsWith("trim")) return "warn";
  if (s.startsWith("watch")) return "warn";
  if (s.startsWith("n/a")) return "neutral";
  return "neutral";
};

/* ── Rating → tone (overall_rating: Strong Buy / Buy / Hold / Sell …) ── */
export const ratingTone = (rating) => {
  if (!rating) return "neutral";
  const s = String(rating).toLowerCase();
  if (s.includes("strong buy") || s === "buy" || s.includes("buy")) return "pos";
  if (s.includes("sell")) return "neg";
  return "neutral";
};

/* ══════════════════════════════════════════════════════════════
   FORMATTERS
══════════════════════════════════════════════════════════════ */

const DASH = "—";

/** "Q3 FY2026" — either part may be missing. */
export const fiscalLabel = (fy, fq) => {
  if (fq != null && fy != null) return `Q${fq} FY${fy}`;
  if (fy != null) return `FY${fy}`;
  if (fq != null) return `Q${fq}`;
  return DASH;
};

/** "bmo" → Before open · "amc" → After close · null → null (caller hides). */
export const reportTimeLabel = (t) => {
  if (!t) return null;
  const map = { bmo: "Before open", amc: "After close" };
  return map[String(t).toLowerCase()] ?? String(t).toUpperCase();
};

/** Date-only display, e.g. "29 Sep 2026". Null-safe. */
export const fmtDate = (d) => {
  if (!d) return DASH;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return DASH;
  return dt.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

/** Signed percentage, 2dp by default. Null-safe. */
export const fmtPct = (v, { decimals = 2, signed = true } = {}) => {
  const n = Number(v);
  if (v == null || Number.isNaN(n)) return DASH;
  const sign = signed && n > 0 ? "+" : "";
  return `${sign}${n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}%`;
};

/** Plain number, null-safe. */
export const fmtNum = (v, decimals = 2) => {
  const n = Number(v);
  if (v == null || Number.isNaN(n)) return DASH;
  return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

/** "price_to_earnings" → "Price To Earnings" (industry_avg is a dynamic dict). */
export const humanizeKey = (key) =>
  String(key)
    .split("_")
    .map((w) => (w.length <= 2 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");

/**
 * `industry_avg` values are RAW TTM ratios — margins / returns arrive as
 * fractions (0.187) and must render as percentages; multiples and coverage
 * ratios render as plain numbers. Formatting is client-side by contract.
 */
const PERCENT_KEY = /(margin|return_on|yield|payout|growth|_pct$)/i;

export const formatRatio = (key, value) => {
  const n = Number(value);
  if (value == null || Number.isNaN(n)) return DASH;
  if (PERCENT_KEY.test(key)) {
    return `${(n * 100).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
  }
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Sub-score bar width for a 0–10 dimension score. */
export const scoreBarWidth = (score) => {
  const n = Number(score);
  if (score == null || Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(100, n * 10));
};

/** Colour a 0–10 sub-score using the same thresholds as the 0–100 bands. */
export const subScoreColor = (score) => {
  const band = bandForScore(Number(score) * 10);
  return band ? band.color : "var(--color-text-muted)";
};

/* ══════════════════════════════════════════════════════════════
   NOTE-REPLICATION SECTIONS  (earnings-review payload, 2026-08-11)

   The endpoint now serves the sections that used to be analyst-written in the
   reference note: `headline`, `latest_quarter` (with its BEAT/MISS/MIXED
   badge), `price_at_review`, `variance_table`, `data_checks` and
   `guidance_vs_actual`.
══════════════════════════════════════════════════════════════ */

/** latest_quarter.badge — derived from the revenue + EPS surprise signs. */
export const badgeTone = (badge) =>
  ({ BEAT: "pos", MISS: "neg", MIXED: "warn" }[String(badge ?? "").toUpperCase()] ?? "muted");

export const badgeColor = (badge) =>
  ({
    BEAT: "var(--color-success)",
    MISS: "var(--color-error)",
    MIXED: "var(--color-warning)",
  }[String(badge ?? "").toUpperCase()] ?? "var(--color-text-muted)");

/**
 * Variance-table rows are heterogeneous: absolute USD, per-share USD, and
 * fields that are ALREADY percentages. The `metric` key decides which.
 *   *_pct            → already a percentage, never multiplied
 *   anything w/ eps  → per-share currency, 2dp
 *   otherwise        → absolute USD, rendered compact
 */
export const varianceKind = (metric) => {
  const m = String(metric ?? "");
  if (/_pct$/.test(m) || /margin/.test(m)) return "pct";
  if (/eps/i.test(m)) return "perShare";
  return "money";
};

/** Index data_checks by metric so a variance row can find its own flag. */
export const dataChecksByMetric = (checks = []) => {
  const out = {};
  (checks ?? []).forEach((c) => {
    if (c?.metric) out[c.metric] = c;
  });
  return out;
};

/** A row the feed says is untrustworthy renders as the note's "n/r". */
export const NOT_RELIABLE = "n/r";

/* ══════════════════════════════════════════════════════════════
   2026-08-17 — every note section is now served

   The NVDA reference note supersedes the MU mock for the SECTION LIST: the
   prose blocks that used to be analyst-written now come from
   `stock_earnings_notes` (seeded once per earnings event) and arrive as
   `key_drivers`, `estimate_model_implications`, `watch_items`, `note.*`,
   `screen_read` — each null until the seeder has run, so render-what's-present
   still applies.

   PRODUCTION TRIM (2026-08-17): `note_meta`, `comps_methodology`,
   `valuation_lens.methodology`, `factor_screen_methodology`,
   `latest_quarter.consensus_source` and `sentiment.transcript_source` were
   removed from the payloads — methodology / provenance footers are not
   rendered in production, and any footer copy is the FE's own static text.
══════════════════════════════════════════════════════════════ */

/** Colour for a band NAME — cutoffs now come from the API's `band_scale`. */
export const bandColorByName = (name) => {
  const hit = SENTIMENT_BANDS.find(
    (b) => b.key.toLowerCase() === String(name ?? "").trim().toLowerCase()
  );
  return hit ? hit.color : "var(--color-text-muted)";
};

/**
 * Build the 0–100 strip from `sentiment.band_scale` rather than the hardcoded
 * cutoffs. Segment width is the band's own span as a share of 100, so a
 * backend rubric change reshapes the strip automatically.
 * Falls back to the canonical rubric when the field is absent.
 */
export const bandStripSegments = (bandScale) => {
  const rows =
    Array.isArray(bandScale) && bandScale.length
      ? bandScale
      : SENTIMENT_BANDS.map((b) => ({ min: b.min, max: b.max, band: b.key }));

  return rows.map((r) => {
    const min = Number(r.min);
    const max = Number(r.max);
    const known = SENTIMENT_BANDS.find(
      (b) => b.key.toLowerCase() === String(r.band ?? "").trim().toLowerCase()
    );
    return {
      key: r.band ?? `${min}-${max}`,
      min,
      max,
      /* +1 because the bands are inclusive integer ranges (0–29 spans 30). */
      width: ((max - min + 1) / 101) * 100,
      color: known ? known.color : "var(--color-text-muted)",
      label: known ? known.label : r.band,
    };
  });
};

/* ── eps_normalization.status ── */
export const epsNormStatusNote = (status) =>
  ({
    applied: null,
    flagged_exceeds_auto_cap:
      "The strip exceeded the 60% auto-cap, so these numbers carry a review flag.",
  }[status] ?? null);

/** Variance rows can carry flag: "non_operating_gain". */
export const isNonOperatingGainRow = (row) => row?.flag === "non_operating_gain";

/* ══════════════════════════════════════════════════════════════
   COMPS FORMATTING (sector peers, thesis valuation lens)
══════════════════════════════════════════════════════════════ */

/** `peg` is a number OR the literal string "n/m" (growth denominator unusable). */
export const fmtPeg = (peg) => {
  if (peg == null) return "—";
  if (typeof peg === "string") return peg;
  const n = Number(peg);
  return Number.isNaN(n) ? "—" : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** A multiple, e.g. 24.9 → "24.9x". */
export const fmtMultiple = (v, decimals = 1) => {
  const n = Number(v);
  if (v == null || Number.isNaN(n)) return "—";
  return `${n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}x`;
};

/** "#74 of 2,918" */
export const fmtRank = (rank, universe) => {
  if (rank == null) return "—";
  const r = `#${Number(rank).toLocaleString()}`;
  return universe != null ? `${r} of ${Number(universe).toLocaleString()}` : r;
};

/* ══════════════════════════════════════════════════════════════
   FACTOR SCREEN  (screen-catalysts factor_screen[])

   score_scale caveat: "zscore" rows carry comparable z-scored dimensions
   (±2-ish, colour by sign). "legacy_raw" rows are on the old raw scale, so
   their dimension cells must be BLANKED — the scales do not mix. Only
   `composite` and `model_rank` are universe-comparable on every row.
══════════════════════════════════════════════════════════════ */
export const SCREEN_DIMENSIONS = Object.freeze([
  { key: "growth", label: "Growth" },
  { key: "profitability", label: "Profitability" },
  { key: "valuation", label: "Valuation" },
  { key: "momentum", label: "Momentum" },
  { key: "risk", label: "Risk" },
  { key: "balance_sheet_quality", label: "Balance sheet" },
]);

export const isComparableScale = (row) => row?.score_scale === "zscore";

/** Z-scores: positive is better on every dimension except none — colour by sign. */
export const zScoreTone = (v) => {
  const n = Number(v);
  if (v == null || Number.isNaN(n)) return "muted";
  if (n > 0.05) return "pos";
  if (n < -0.05) return "neg";
  return "neutral";
};

export const fmtZScore = (v) => {
  const n = Number(v);
  if (v == null || Number.isNaN(n)) return "—";
  return `${n >= 0 ? "+" : "−"}${Math.abs(n).toFixed(2)}`;
};

/* ── catalyst_calendar[].source — "hard date" vs company-stated ── */
export const catalystSourceLabel = (source) => {
  if (!source) return null;
  if (/hard date/i.test(source)) return "hard date";
  if (/transcript/i.test(source)) return "company-stated — verify before trading";
  return source;
};
